import { createEndpoint } from "../createEndpoint";
import { HttpClient } from "../httpClient";
import {
  LoginResponseSchema,
  RegisterResponseSchema,
  AuthCheckResponseSchema,
  AuthSuccessMessageResponseSchema,
  CheckEmailResponseSchema,
  ConvexJwtTokenResponseSchema,
  type LoginRequest,
  type RegisterRequest,
  type ForgotPasswordRequest,
  type ResetPasswordRequest,
  type VerifyEmailRequest,
  type CheckEmailRequest,
} from "../schemas/auth-v2";

/**
 * Creates the public auth API endpoints.
 *
 * Public Endpoints:
 * - login: POST /api/v2/auth/login
 * - register: POST /api/v2/auth/register
 * - checkEmail: POST /api/v2/auth/check-email
 * - forgotPassword: POST /api/v2/auth/forgot-password
 * - resetPassword: POST /api/v2/auth/reset-password
 * - verifyEmail: POST /api/v2/auth/verify-email
 *
 * Authenticated Endpoints:
 * - check: GET /api/v2/auth/check
 * - sendVerification: POST /api/v2/auth/send-verification
 * - jwtToken: POST /api/v2/auth/jwt-token
 */
export function createPublicAuthApi(http: HttpClient, baseUrl: string) {
  const authBaseUrl = `${baseUrl}/auth`;

  return {
    /**
     * Authenticate a user (client or expert) and receive an access token.
     *
     * POST /api/v2/auth/login
     *
     * @param body - Login credentials (email, password)
     * @returns User info, type, and auth token
     *
     * @example
     * ```ts
     * const result = await api.public.auth.login({
     *   body: { email: "user@example.com", password: "password123" }
     * });
     * console.log(result.token); // Bearer token
     * console.log(result.type); // "client" or "expert"
     * ```
     */
    login: createEndpoint<
      typeof LoginResponseSchema._output,
      LoginRequest
    >(http, "POST", "/login", {
      base: authBaseUrl,
      responseSchema: LoginResponseSchema,
    }),

    /**
     * Register a new client account.
     *
     * POST /api/v2/auth/register
     *
     * @param body - Registration data (email, password, first_name, last_name, company_name, company_website?)
     * @returns User info, type ("client"), and auth token
     *
     * @example
     * ```ts
     * const result = await api.public.auth.register({
     *   body: {
     *     email: "john@acme.com",
     *     password: "SecurePass123!",
     *     first_name: "John",
     *     last_name: "Doe",
     *     company_name: "Acme Inc",
     *     company_website: "https://acme.com"
     *   }
     * });
     * console.log(result.token); // Bearer token
     * ```
     */
    register: createEndpoint<
      typeof RegisterResponseSchema._output,
      RegisterRequest
    >(http, "POST", "/register", {
      base: authBaseUrl,
      responseSchema: RegisterResponseSchema,
    }),

    /**
     * Check the current authentication status and get user details.
     * Requires a valid Bearer token.
     *
     * GET /api/v2/auth/check
     *
     * @returns Full user details including client/expert info, organization, validation status
     *
     * @example
     * ```ts
     * const result = await api.public.auth.check();
     * if (result.type === "client") {
     *   console.log(result.client?.company_name);
     *   console.log(result.organization?.role);
     * } else {
     *   console.log(result.expert?.slug);
     *   console.log(result.expert?.status);
     * }
     * ```
     */
    check: createEndpoint<typeof AuthCheckResponseSchema._output>(
      http,
      "GET",
      "/check",
      {
        base: authBaseUrl,
        responseSchema: AuthCheckResponseSchema,
      }
    ),

    /**
     * Check if an email address exists in the system and whether it belongs to a client.
     *
     * POST /api/v2/auth/check-email
     *
     * @param body - Email address to check
     * @returns Whether email exists and if it belongs to a client
     *
     * @example
     * ```ts
     * const result = await api.public.auth.checkEmail({
     *   body: { email: "user@example.com" }
     * });
     * if (result.exists && result.has_client) {
     *   console.log("Email belongs to a client");
     * } else if (result.exists) {
     *   console.log("Email exists but not a client");
     * } else {
     *   console.log("Email not found");
     * }
     * ```
     */
    checkEmail: createEndpoint<
      typeof CheckEmailResponseSchema._output,
      CheckEmailRequest
    >(http, "POST", "/check-email", {
      base: authBaseUrl,
      responseSchema: CheckEmailResponseSchema,
    }),

    /**
     * Request a password reset link to be sent to the user's email.
     *
     * POST /api/v2/auth/forgot-password
     *
     * @param body - Email address
     * @returns Success message (always returns success for security)
     *
     * @example
     * ```ts
     * const result = await api.public.auth.forgotPassword({
     *   body: { email: "user@example.com" }
     * });
     * console.log(result.message); // "If an account with that email exists..."
     * ```
     */
    forgotPassword: createEndpoint<
      typeof AuthSuccessMessageResponseSchema._output,
      ForgotPasswordRequest
    >(http, "POST", "/forgot-password", {
      base: authBaseUrl,
      responseSchema: AuthSuccessMessageResponseSchema,
    }),

    /**
     * Reset the user's password using a valid token received via email.
     *
     * POST /api/v2/auth/reset-password
     *
     * @param body - Email, token, password, password_confirmation
     * @returns Success message
     *
     * @example
     * ```ts
     * const result = await api.public.auth.resetPassword({
     *   body: {
     *     email: "user@example.com",
     *     token: "reset-token-from-email",
     *     password: "NewSecurePass123!",
     *     password_confirmation: "NewSecurePass123!"
     *   }
     * });
     * console.log(result.message); // "Password has been reset successfully."
     * ```
     */
    resetPassword: createEndpoint<
      typeof AuthSuccessMessageResponseSchema._output,
      ResetPasswordRequest
    >(http, "POST", "/reset-password", {
      base: authBaseUrl,
      responseSchema: AuthSuccessMessageResponseSchema,
    }),

    /**
     * Verify a user's email address using the token from the verification email.
     *
     * POST /api/v2/auth/verify-email
     *
     * @param body - Email and verification token
     * @returns Success message
     *
     * @example
     * ```ts
     * const result = await api.public.auth.verifyEmail({
     *   body: {
     *     email: "user@example.com",
     *     token: "verification-token-from-email"
     *   }
     * });
     * console.log(result.message); // "Email verified successfully."
     * ```
     */
    verifyEmail: createEndpoint<
      typeof AuthSuccessMessageResponseSchema._output,
      VerifyEmailRequest
    >(http, "POST", "/verify-email", {
      base: authBaseUrl,
      responseSchema: AuthSuccessMessageResponseSchema,
    }),

    /**
     * Resend the email verification link to the authenticated user.
     * Requires a valid Bearer token.
     *
     * POST /api/v2/auth/send-verification
     *
     * @returns Success message
     *
     * @example
     * ```ts
     * const result = await api.public.auth.sendVerification();
     * console.log(result.message); // "Verification email sent successfully."
     * ```
     */
    sendVerification: createEndpoint<
      typeof AuthSuccessMessageResponseSchema._output
    >(http, "POST", "/send-verification", {
      base: authBaseUrl,
      responseSchema: AuthSuccessMessageResponseSchema,
    }),

    /**
     * Generate a Convex-compatible JWT for the currently authenticated user.
     * Requires a valid Bearer token from /auth/login or /auth/register.
     *
     * POST /api/v2/auth/jwt-token
     *
     * @returns Convex JWT payload including token and application id
     */
    jwtToken: createEndpoint<
      typeof ConvexJwtTokenResponseSchema._output
    >(http, "POST", "/jwt-token", {
      base: authBaseUrl,
      responseSchema: ConvexJwtTokenResponseSchema,
    }),
  };
}

